import React from 'react'

import {
  type ArrowPaths,
  SegmentRouteArrow,
} from '~/components/features/SegmentRouteArrow'
import { type FiveNumber } from '~/utils'

const arrowWidth = 4
const arrowHeadH = 16
const arrowHeadV = 16
const borderWidth = 2

const arrowHeadHalfH = arrowHeadH / 2
const hypotenuse = (arrowHeadHalfH ** 2 + arrowHeadV ** 2) ** (1 / 2)
const borderDiffH =
  (borderWidth * arrowHeadHalfH * (1 + hypotenuse / arrowHeadHalfH)) /
  arrowHeadV
const borderDiffV = (borderWidth * hypotenuse) / arrowHeadHalfH

const computeVerticalArrowPaths = (
  x: number,
  fromY: number,
  toY: number,
): ArrowPaths => {
  const halfWidth = arrowWidth / 2
  const arrowOverhang = (arrowHeadH - arrowWidth) / 2
  const headBaseY = toY + arrowHeadV

  const arrowDraw = [
    `M ${x - halfWidth} ${fromY}`,
    `L ${x - halfWidth} ${headBaseY}`,
    `L ${x - halfWidth - arrowOverhang} ${headBaseY}`,
    `L ${x} ${toY}`,
    `L ${x + halfWidth + arrowOverhang} ${headBaseY}`,
    `L ${x + halfWidth} ${headBaseY}`,
    `L ${x + halfWidth} ${fromY}`,
    'Z',
  ].join(' ')

  const borderDraw = [
    `M ${x - halfWidth - borderWidth} ${fromY + borderWidth}`,
    `L ${x - halfWidth - borderWidth} ${headBaseY + borderWidth}`,
    `L ${x - halfWidth - arrowOverhang - borderDiffH} ${headBaseY + borderWidth}`,
    `L ${x} ${toY - borderDiffV}`,
    `L ${x + halfWidth + arrowOverhang + borderDiffH} ${headBaseY + borderWidth}`,
    `L ${x + halfWidth + borderWidth} ${headBaseY + borderWidth}`,
    `L ${x + halfWidth + borderWidth} ${fromY + borderWidth}`,
    'Z',
  ].join(' ')

  return { arrowDraw, borderDraw }
}

export type FiveSegmentRoute = {
  fromSegmentNumber: FiveNumber
  toSegmentNumber: FiveNumber
}

type ArrowRouteConfig = {
  xOffset: number
  segmentRoute: FiveSegmentRoute
}

const arrowRouteConfigs: ReadonlyArray<ArrowRouteConfig> = [
  {
    xOffset: 0,
    segmentRoute: { fromSegmentNumber: 2, toSegmentNumber: 1 },
  },
  {
    xOffset: -30,
    segmentRoute: { fromSegmentNumber: 3, toSegmentNumber: 2 },
  },
  {
    xOffset: 0,
    segmentRoute: { fromSegmentNumber: 4, toSegmentNumber: 2 },
  },
  {
    xOffset: 30,
    segmentRoute: { fromSegmentNumber: 5, toSegmentNumber: 2 },
  },
]

export const fiveSegmentArrowRoutes: ReadonlyArray<FiveSegmentRoute> =
  arrowRouteConfigs.map((config) => config.segmentRoute)

type ArrowsProps = {
  activeRoute: FiveSegmentRoute | null
  centerX: number
  getSegmentCenterY: (fiveNumber: FiveNumber) => number
  onRouteClick: (segmentRoute: FiveSegmentRoute) => void
  segmentHeight: number
}

export const Arrows = ({
  activeRoute,
  centerX,
  getSegmentCenterY,
  onRouteClick,
  segmentHeight,
}: ArrowsProps) => {
  const hasActiveRoute = activeRoute !== null

  return (
    <g stroke="var(--colorFontLighter)" strokeOpacity={0} strokeWidth={12}>
      {arrowRouteConfigs.map(({ segmentRoute, xOffset }) => {
        const isActive =
          activeRoute?.fromSegmentNumber === segmentRoute.fromSegmentNumber &&
          activeRoute?.toSegmentNumber === segmentRoute.toSegmentNumber

        const arrowX = centerX + xOffset
        const fromY = getSegmentCenterY(segmentRoute.fromSegmentNumber)
        const arrowTipOffset = segmentHeight * 0.3
        const toY =
          getSegmentCenterY(segmentRoute.toSegmentNumber) + arrowTipOffset
        const paths = computeVerticalArrowPaths(arrowX, fromY, toY)

        return (
          <SegmentRouteArrow
            key={`${segmentRoute.fromSegmentNumber}-${segmentRoute.toSegmentNumber}`}
            isActive={isActive}
            isInActive={hasActiveRoute && !isActive}
            paths={paths}
            onClick={() => onRouteClick(segmentRoute)}
          />
        )
      })}
    </g>
  )
}
